import fp from 'lodash/fp';
import { useCallback } from 'react';
import { connect, useSelector, useDispatch } from 'react-redux';
import { push, RouterRootState } from 'connected-react-router';
import { Path, LocationState, LocationDescriptorObject } from 'history';

import { useCallbackNoWarn, useMemoNoWarn } from 'Modules/ui2/hooks';

import { ActionCreator, ActionCreatorWithPath } from './state';


export const withConnectedRouter = connect(
    ({ router }: RouterRootState) => ({
        location: router.location,
    }),
    { push },
);

type SelectorType<TState> = string | ((state: TState) => any);

const useSelectorCustom = <TReturn = any, TState = any>(
    selector: SelectorType<TState>,
    deps?: any[],
): TReturn => {
    const selectorFunc = useCallbackNoWarn(
        state => fp.isString(selector) ? fp.get(selector, state) : selector(state),
        deps || [selector],
    );
    return useSelector<TState, TReturn>(selectorFunc);
};

type ReturnDispatchType<TPayload> =
    ReturnType<ActionCreator<TPayload>> | ReturnType<ActionCreatorWithPath<TPayload>>;

type FunctionActionType<TValue, TPayload> = (value: TValue) => ReturnDispatchType<TPayload>;

type CheckParameters<TValue, TReturnType = (value: TValue) => void> =
TValue extends object | string | number
    ? TReturnType
    : () => void;

type ReturnActionWithPath<TPayload> = (path: string, value: TPayload) => void;

export function useAction<TValue, TPayload>(
    action: FunctionActionType<TValue, TPayload>,
    deps?: any[],
): CheckParameters<TValue>;
export function useAction<TPayload>(
    action: ActionCreator<TPayload>,
    deps?: any[],
): CheckParameters<TPayload>;
export function useAction<TPayload>(
    action: ActionCreatorWithPath<TPayload>,
    deps?: any[],
): CheckParameters<TPayload, ReturnActionWithPath<TPayload>>;
export function useAction<TValue, TPayload>(
    action: ActionCreator<TPayload> |
    ActionCreatorWithPath<TPayload> |
    FunctionActionType<TValue, TPayload>,
    deps?: any[],
) {
    const dispatch = useDispatch();
    return useMemoNoWarn(
        () => fp.pipe(action, dispatch),
        deps ? [dispatch, ...deps] : [action, dispatch],
    );
}

export const useConnectedRouter = () => {
    const location = useSelectorCustom('router.location');

    const dispatch = useDispatch();
    const pushTo = useCallback(
        (path: Path | LocationDescriptorObject<LocationState>, state?: LocationState) => {
            if (typeof path === 'string')
                dispatch(push(path, state));
            else
                dispatch(push(path));
        },
        [dispatch],
    );

    return {
        location,
        push: pushTo,
    };
};

export { connect, useDispatch, useSelectorCustom as useSelector };
